import React, { useState, useEffect } from 'react';
import { ShowHint } from './ShowHint';
import { useGameAudio } from './GameAudioProvider';

interface QuestionData {
  id: number;
  title: string;
  aiPersonality: string;
  aiEmoji: string;
  theme: string;
  playerSymbol: string;
  aiSymbol: string;
  description: string;
}

interface GameplayScreenProps {
  question: QuestionData;
  questionNumber: number;
  totalQuestions: number;
  onNextQuestion?: () => void;
  onQuestionComplete: (score: number, won: boolean) => void;
}

type Player = 'player' | 'ai' | null;
type Board = (Player)[];

const GameplayScreen: React.FC<GameplayScreenProps> = ({
  question,
  questionNumber,
  totalQuestions,
  onNextQuestion,
  onQuestionComplete
}) => {
  const [board, setBoard] = useState<Board>(Array(9).fill(null));
  const [currentPlayer, setCurrentPlayer] = useState<'player' | 'ai'>('player');
  const [gameStatus, setGameStatus] = useState<'playing' | 'won' | 'lost' | 'draw'>('playing');
  const [score, setScore] = useState(0);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const [moveCount, setMoveCount] = useState(0);
  const [showGameComplete, setShowGameComplete] = useState(false);
  const [gameResult, setGameResult] = useState<{ won: boolean; finalScore: number } | null>(null);
  const { kinzySpeak } = useGameAudio();

  const playerSymbol = question.playerSymbol;
  const aiSymbol = question.aiSymbol;
  const aiEmoji = question.aiEmoji;
  const aiPersonality = question.aiPersonality;

  // Timer
  useEffect(() => {
    // Voice narration when new question starts
    kinzySpeak(`Battle ${questionNumber} of ${totalQuestions}. ${question.description}. You are ${playerSymbol}, your opponent is ${aiSymbol}. Make your move!`);
    
  }, [question, questionNumber, totalQuestions, playerSymbol, aiSymbol, kinzySpeak]);


  useEffect(() => {
    if (gameStatus === 'playing') {
      const timer = setInterval(() => {
        setTimeElapsed(prev => prev + 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [gameStatus]);

  // Check for winner
  const checkWinner = (board: Board): Player => {
    const lines = [
      [0, 1, 2], [3, 4, 5], [6, 7, 8], // rows
      [0, 3, 6], [1, 4, 7], [2, 5, 8], // columns
      [0, 4, 8], [2, 4, 6] // diagonals
    ];

    for (const [a, b, c] of lines) {
      if (board[a] && board[a] === board[b] && board[a] === board[c]) {
        return board[a];
      }
    }

    return board.includes(null) ? null : 'draw' as Player;
  };

  // AI move logic
  const makeAIMove = (currentBoard: Board): number => {
    // Simple AI: try to win, then block, then random
    const availableMoves = currentBoard.map((cell, index) => cell === null ? index : null).filter(val => val !== null) as number[];
    
    // Try to win
    for (const move of availableMoves) {
      const testBoard = [...currentBoard];
      testBoard[move] = 'ai';
      if (checkWinner(testBoard) === 'ai') {
        return move;
      }
    }
    
    // Try to block player
    for (const move of availableMoves) {
      const testBoard = [...currentBoard];
      testBoard[move] = 'player';
      if (checkWinner(testBoard) === 'player') {
        return move;
      }
    }
    
    // Random move
    return availableMoves[Math.floor(Math.random() * availableMoves.length)];
  };

  // Handle player move
  const handleCellClick = (index: number) => {
    if (board[index] || gameStatus !== 'playing' || currentPlayer !== 'player') {
      return;
    }

    const newBoard = [...board];
    newBoard[index] = 'player';
    setMoveCount(prev => prev + 1);
    setBoard(newBoard);

    const winner = checkWinner(newBoard);
    if (winner) {
      handleGameEnd(winner, newBoard);
    } else {
      setCurrentPlayer('ai');
      // AI move after delay
      setTimeout(() => {
        const aiMove = makeAIMove(newBoard);
        if (aiMove !== undefined) {
          const aiBoard = [...newBoard];
          aiBoard[aiMove] = 'ai';
          setMoveCount(prev => prev + 1);
          setBoard(aiBoard);
          
          const aiWinner = checkWinner(aiBoard);
          if (aiWinner) {
            handleGameEnd(aiWinner, aiBoard);
          } else {
            // Announce AI move
            setTimeout(() => {
              kinzySpeak(`${aiPersonality} placed their symbol. Your turn!`);
            }, 1200);
            
            setCurrentPlayer('player');
          }
        }
      }, 1000);
    }
  };

  const handleGameEnd = (winner: Player, finalBoard: Board) => {
    if (winner === 'player') {
      setGameStatus('won');
      setScore(prev => prev + 100);
      kinzySpeak('Victory! You won this battle!');
      setShowCelebration(true);
      setTimeout(() => setShowCelebration(false), 2000);
    } else if (winner === 'ai') {
      setGameStatus('lost');
      kinzySpeak(`The ${aiPersonality} wins this round. Try again!`);
    } else {
      kinzySpeak('It\'s a draw! Well played by both!');
      setGameStatus('draw');
      setScore(prev => prev + 50);
    }
    
    // Show completion UI after a brief delay
    setTimeout(() => {
      setShowGameComplete(true);
      const timeBonus = Math.max(0, 300 - timeElapsed);
      const finalScore = score + timeBonus + (winner === 'player' ? 100 : winner === 'draw' ? 50 : 0);
      setGameResult({ won: winner === 'player', finalScore });
    }, 2000);
  };

  const resetGame = () => {
    setBoard(Array(9).fill(null));
    setCurrentPlayer('player');
    setGameStatus('playing');
    setTimeElapsed(0);
    setMoveCount(0);
    setShowGameComplete(false);
    setGameResult(null);
  };

  const handleContinue = () => {
    if (gameResult) {
      onQuestionComplete(gameResult.finalScore, gameResult.won);
      resetGame();
      if (onNextQuestion) {
        onNextQuestion();
      }
    }
  };

  const currentHint = {
    text: gameStatus === 'playing' 
      ? "Look for three in a row! Try to block your opponent while creating your own winning line."
      : "The battle will automatically continue to the next kingdom!",
    emoji: "🎯",
    category: "Strategy Tip"
  };

  return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 opacity-10">
          {Array.from({ length: 30 }, (_, i) => (
            <div
              key={i}
              className="absolute bg-gradient-to-r from-yellow-300 to-pink-300 rounded-full animate-bounce shadow-lg"
              style={{
                left: Math.random() * 100 + '%',
                top: Math.random() * 100 + '%',
                width: Math.random() * 20 + 8 + 'px',
                height: Math.random() * 20 + 8 + 'px',
                animationDelay: Math.random() * 3 + 's',
                animationDuration: Math.random() * 2 + 2 + 's'
              }}
            />
          ))}
        </div>

        {/* Stats bar - positioned near back and sound buttons */}
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-40 px-4">
          <div className="flex justify-center space-x-6 mb-4">
            <div className="text-center">
              <div className="text-white font-bold text-xl drop-shadow-lg">{score}</div>
              <div className="text-white/80 text-xs font-medium">Score</div>
            </div>
            <div className="text-center">
              <div className="text-white font-bold text-xl drop-shadow-lg">{moveCount}</div>
              <div className="text-white/80 text-xs font-medium">Moves</div>
            </div>
            <div className="text-center">
              <div className="text-white font-bold text-xl drop-shadow-lg">{Math.floor(timeElapsed / 60)}:{(timeElapsed % 60).toString().padStart(2, '0')}</div>
              <div className="text-white/80 text-xs font-medium">Time</div>
            </div>
          </div>

        </div>

        <div className="relative z-10 px-4 pt-20 pb-2">
          {/* Question title */}
          <div className="text-center mb-6">
            <div className="mb-4">
              <div className="mb-2">
                <h2 className="text-lg md:text-xl font-bold text-white drop-shadow-lg">
                  Q{questionNumber}/{totalQuestions}: {question.title}
                </h2>
              </div>
              <div>
                <p className="text-white/90 text-sm md:text-base font-medium drop-shadow-md">
                  {question.description}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Game board */}
        <div className="flex flex-col items-center justify-center px-4 -mt-4">
          <div className="bg-gradient-to-br from-white/20 to-white/10 backdrop-blur-lg rounded-3xl p-6 shadow-2xl border border-white/20">
            <div className="grid grid-cols-3 gap-3 w-80 h-80 mx-auto">
              {board.map((cell, index) => (
                <button
                  key={index}
                  onClick={() => handleCellClick(index)}
                  disabled={cell !== null || gameStatus !== 'playing' || currentPlayer !== 'player'}
                  className={`
                    w-24 h-24 rounded-xl border-3 border-white/40 
                    flex items-center justify-center text-2xl md:text-3xl font-bold
                    transition-all duration-200 transform
                    shadow-lg
                    ${
                      cell === null && gameStatus === 'playing' && currentPlayer === 'player'
                        ? 'bg-gradient-to-br from-white/30 to-white/20 hover:from-white/40 hover:to-white/30 hover:scale-110 active:scale-95 cursor-pointer border-yellow-300/50'
                        : 'bg-gradient-to-br from-white/15 to-white/10'
                    }
                    ${
                      cell === 'player' ? 'bg-gradient-to-br from-blue-500/60 to-blue-600/60 text-white shadow-blue-500/30' :
                      cell === 'ai' ? 'bg-gradient-to-br from-purple-500/60 to-purple-600/60 text-white shadow-purple-500/30' : ''
                    }
                  `}
                >
                  <span className="select-none text-center leading-tight text-6xl md:text-7xl lg:text-8xl" style={{ fontSize: 'clamp(3.5rem, 10vw, 6rem)' }}>{cell === 'player' ? playerSymbol : cell === 'ai' ? aiSymbol : ''}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Game status */}
          <div className="mt-4 text-center">
            {gameStatus === 'playing' && (
              <div className="">
                <p className="text-white text-lg font-bold drop-shadow-lg">
                  {currentPlayer === 'player' ? 'Your turn!' : `${aiPersonality} is thinking...`}
                </p>
              </div>
            )}
            
            {gameStatus === 'won' && (
              <div className="space-y-4">
                <div className="text-6xl animate-bounce">🎉</div>
                <div className="">
                  <p className="text-white text-xl font-bold drop-shadow-lg">Victory!</p>
                </div>
                <p className="text-white/90 text-base font-medium">You defeated the {aiPersonality}!</p>
              </div>
            )}
            
            {gameStatus === 'lost' && (
              <div className="space-y-4">
                <div className="text-6xl">{aiEmoji}</div>
                <div className="">
                  <p className="text-white text-xl font-bold drop-shadow-lg">The {aiPersonality} wins!</p>
                </div>
                <p className="text-white/90 text-base font-medium">Try again, brave warrior!</p>
              </div>
            )}
            
            {gameStatus === 'draw' && (
              <div className="space-y-4">
                <div className="text-6xl">🤝</div>
                <div className="">
                  <p className="text-white text-xl font-bold drop-shadow-lg">It's a draw!</p>
                </div>
                <p className="text-white/90 text-base font-medium">Well played by both!</p>
              </div>
            )}
          </div>
        </div>

        {/* Game completion overlay */}
        {showGameComplete && gameResult && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center">
              <div className="text-6xl mb-4">
                {gameResult.won ? '🎉' : gameStatus === 'draw' ? '🤝' : '😔'}
              </div>
              <h3 className="text-2xl font-bold mb-4">
                {gameResult.won ? 'Victory!' : gameStatus === 'draw' ? "It's a Draw!" : 'Try Again!'}
              </h3>
              <p className="text-gray-600 mb-6">
                {gameResult.won ? `You defeated the ${aiPersonality}!` : 
                 gameStatus === 'draw' ? 'Well played by both!' : 
                 `The ${aiPersonality} wins this round!`}
              </p>
              <div className="text-3xl font-bold text-blue-600 mb-6">
                Score: {gameResult.finalScore}
              </div>
              <button
                onClick={handleContinue}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-6 rounded-xl font-bold text-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200 active:scale-95"
              >
                {questionNumber < totalQuestions ? 'Next Battle ⚔️' : 'Complete Adventure 🏆'}
              </button>
              {gameStatus !== 'playing' && questionNumber < totalQuestions && (
                <button onClick={resetGame} className="mt-3 w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-2 px-4 rounded-xl font-semibold text-sm hover:shadow-lg transform hover:scale-105 transition-all duration-200 active:scale-95">
                  Play This Battle Again 🔄
                </button>
              )}
            </div>
          </div>
        )}

        {/* Celebration overlay */}
        {showCelebration && (
          <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-50">
            <div className="text-8xl animate-bounce">
              🎊🏆🎊
            </div>
          </div>
        )}

        {/* Hint system */}
        <ShowHint hint={currentHint} />
      </div>
  );
};

export { GameplayScreen };